import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { isToolSemanticLogUiEnabled } from "./toolResultSemanticLog";
const stringifyDebugValue = (value) => {
    if (value === undefined || value === null) {
        return "";
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed)
            return "";
        try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
        }
        catch {
            return value;
        }
    }
    try {
        return JSON.stringify(value, null, 2);
    }
    catch {
        return String(value);
    }
};
export const hasToolInputOutputPayload = (input, output) => {
    if (!isToolSemanticLogUiEnabled()) {
        return false;
    }
    return (stringifyDebugValue(input).trim().length > 0 ||
        stringifyDebugValue(output).trim().length > 0);
};
const copyStateClass = (state) => {
    if (state === "copied") {
        return "border-green-300 text-green-700 bg-green-50";
    }
    if (state === "failed") {
        return "border-red-300 text-red-700 bg-red-50";
    }
    return "border-gray-300 text-gray-700 hover:bg-gray-200";
};
const copyStateLabel = (state) => {
    if (state === "copied")
        return "Copied";
    if (state === "failed")
        return "Copy failed";
    return "Copy";
};
export const ToolInputOutputPanel = ({ input, output, className, }) => {
    const inputText = React.useMemo(() => stringifyDebugValue(input), [input]);
    const outputText = React.useMemo(() => stringifyDebugValue(output), [output]);
    const hasInput = inputText.trim().length > 0;
    const hasOutput = outputText.trim().length > 0;
    const [inputCopyState, setInputCopyState] = React.useState("idle");
    const [outputCopyState, setOutputCopyState] = React.useState("idle");
    const copyText = React.useCallback(async (text, setState) => {
        try {
            if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
                throw new Error("Clipboard API unavailable");
            }
            await navigator.clipboard.writeText(text);
            setState("copied");
            window.setTimeout(() => setState("idle"), 1400);
        }
        catch {
            setState("failed");
            window.setTimeout(() => setState("idle"), 1800);
        }
    }, []);
    if (!isToolSemanticLogUiEnabled() || (!hasInput && !hasOutput)) {
        return null;
    }
    return (_jsxs("div", { className: className ?? "w-full py-[1px]", children: [hasInput && (_jsxs("div", { className: "w-full pb-[4px]", children: [_jsxs("div", { className: "flex items-center justify-between gap-2 pb-[4px]", children: [_jsx("p", { className: "text-[10px] leading-[1.5] font-bold text-gray-700", children: "Tool Input" }), _jsx("button", { type: "button", onClick: () => {
                                    void copyText(inputText, setInputCopyState);
                                }, className: `rounded border px-2 py-[1px] text-[10px] leading-[1.4] ${copyStateClass(inputCopyState)}`, "aria-label": "Copy tool input", children: copyStateLabel(inputCopyState) })] }), _jsx("pre", { className: "w-full max-h-52 overflow-auto whitespace-pre-wrap break-words rounded border border-gray-300 bg-white px-2 py-[6px] text-[10px] leading-[1.45] text-gray-800", children: inputText })] })), hasOutput && (_jsxs("div", { className: "w-full", children: [_jsxs("div", { className: "flex items-center justify-between gap-2 pb-[4px]", children: [_jsx("p", { className: "text-[10px] leading-[1.5] font-bold text-gray-700", children: "Tool Output" }), _jsx("button", { type: "button", onClick: () => {
                                    void copyText(outputText, setOutputCopyState);
                                }, className: `rounded border px-2 py-[1px] text-[10px] leading-[1.4] ${copyStateClass(outputCopyState)}`, "aria-label": "Copy tool output", children: copyStateLabel(outputCopyState) })] }), _jsx("pre", { className: "w-full max-h-52 overflow-auto whitespace-pre-wrap break-words rounded border border-gray-300 bg-white px-2 py-[6px] text-[10px] leading-[1.45] text-gray-800", children: outputText })] }))] }));
};
